import React, {useMemo} from 'react';
import {App, Button, Collapse, Space, Tag, Typography} from 'antd';
import {CopyOutlined} from '@ant-design/icons';
import SegmentEditor from '../../components/bot/SegmentEditor';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import {parsePromptToSegments, PromptSectionResponse, SegmentModel} from '../../services/botService';

const {Text} = Typography;

interface AgentExecutionPromptViewProps {
    sections: PromptSectionResponse[];
    apiKeyId: number;
}

const sectionTypeLabelMap: Record<PromptSectionResponse['sectionType'], string> = {
    SUBMITTED_PROMPT: '提交内容',
    SYSTEM_PROMPT: 'System',
    USER_CONTEXT: '上下文',
    STARTING_GUN: '发令枪',
    TOOL_CALL: '工具调用',
    TOOL_RESULT: '工具结果',
    FINAL_OUTPUT: '最终输出',
    LEGACY_PROMPT: 'Prompt'
};

const sectionTypeColorMap: Record<PromptSectionResponse['sectionType'], string> = {
    SUBMITTED_PROMPT: 'blue',
    SYSTEM_PROMPT: 'purple',
    USER_CONTEXT: 'cyan',
    STARTING_GUN: 'gold',
    TOOL_CALL: 'orange',
    TOOL_RESULT: 'green',
    FINAL_OUTPUT: 'magenta',
    LEGACY_PROMPT: 'default'
};

const blockStyle: React.CSSProperties = {
    backgroundColor: '#2a2a2a',
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #404040',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '12px'
};

const rawContentStyle: React.CSSProperties = {
    ...blockStyle,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
    fontFamily: 'monospace'
};

const toolNameMap: Record<string, string> = {
    get_kline: '获取K线数据',
    get_position: '查询持仓',
    get_balance: '查询账户',
    trade: '执行交易',
    cancel_order: '撤销订单',
    set_stop_order: '设置止损止盈',
    get_candle_patterns: 'K线形态识别',
    get_zone_patterns: '价格区域分析',
    get_long_short_ratio: '检索多空比',
    get_radar: '市场雷达扫描',
    submit_final_output: '提交最终结果'
};

const AgentExecutionPromptView: React.FC<AgentExecutionPromptViewProps> = ({
                                                                               sections,
                                                                               apiKeyId
                                                                           }) => {
    const {message: messageApi} = App.useApp();
    const visibleSections = useMemo(
        () => sections.filter(section => section.sectionType !== 'TOOL_CALL' && section.sectionType !== 'STARTING_GUN'),
        [sections]
    );

    const parsedSectionMap = useMemo<Record<string, SegmentModel[]>>(() => {
        return visibleSections.reduce<Record<string, SegmentModel[]>>((acc, section) => {
            if (section.displayMode === 'SEGMENTS' && section.content?.includes('===')) {
                acc[section.sectionId] = parsePromptToSegments(section.content);
            }
            return acc;
        }, {});
    }, [visibleSections]);

    const defaultActiveKeys = useMemo<string[]>(() => {
        if (visibleSections.length === 0) {
            return [];
        }
        const latestStepSection = [...visibleSections]
            .filter(section => ['TOOL_RESULT', 'FINAL_OUTPUT'].includes(section.sectionType))
            .sort((a, b) => (b.orderNo || 0) - (a.orderNo || 0))[0];

        return visibleSections
            .filter(section =>
                section.sectionType === 'SUBMITTED_PROMPT'
                || section.sectionType === 'USER_CONTEXT'
                || section.sectionId === latestStepSection?.sectionId
            )
            .map(section => section.sectionId);
    }, [visibleSections]);

    const handleCopy = async (content: string, title: string) => {
        try {
            await navigator.clipboard.writeText(content || '');
            messageApi.success(`${title}已复制到剪贴板`);
        } catch {
            messageApi.error('复制失败');
        }
    };

    return (
        <Collapse
            defaultActiveKey={defaultActiveKeys}
            size="small"
            ghost
            items={visibleSections.map(section => {
                const parsedSegments = parsedSectionMap[section.sectionId] || [];
                const useSegments = section.displayMode === 'SEGMENTS' && parsedSegments.length > 0;
                const useMarkdown = section.sectionType === 'TOOL_RESULT';
                const mergedTagLabel = useMarkdown && section.functionName
                    ? (toolNameMap[section.functionName] || section.functionName)
                    : null;

                return {
                    key: section.sectionId,
                    label: (
                        <Space size="small" wrap>
                            <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                {section.title}
                            </Text>
                            <Tag color={sectionTypeColorMap[section.sectionType] || 'default'}
                                 style={{marginInlineEnd: 0}}>
                                {mergedTagLabel || sectionTypeLabelMap[section.sectionType] || section.sectionType}
                            </Tag>
                            {!mergedTagLabel && section.functionName && (
                                <Tag style={{marginInlineEnd: 0}}>
                                    {section.functionName}
                                </Tag>
                            )}
                        </Space>
                    ),
                    children: (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined/>}
                                    onClick={() => handleCopy(section.content || '', section.title || '内容')}
                                    style={{color: 'rgba(255, 255, 255, 0.6)'}}
                                >
                                    复制
                                </Button>
                            </div>
                            {useSegments ? (
                                <div style={blockStyle}>
                                    <SegmentEditor
                                        segments={parsedSegments}
                                        readOnly={true}
                                        apiKeyId={apiKeyId}
                                    />
                                </div>
                            ) : useMarkdown ? (
                                <div style={blockStyle}>
                                    <MarkdownRenderer
                                        content={section.content || '暂无内容'}
                                        showCopyButton={false}
                                        segmentTitle={section.title}
                                        fullContent={section.content || '暂无内容'}
                                    />
                                </div>
                            ) : (
                                <div style={rawContentStyle}>
                                    {section.content || '暂无内容'}
                                </div>
                            )}
                        </div>
                    )
                };
            })}
        />
    );
};

export default AgentExecutionPromptView;
